import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import {
  CreditAccountDTO,
  CreditTransactionDTO,
  TransactionType,
  transactionTypeLabels,
  serviceTypeLabels,
  formatMoney,
} from '../../types/billing';
import { formatServerUtcAsIst, istDaysToServerUtc } from './closeBooksRules';
import IstDateRange from './IstDateRange';
import { isRefundable } from './creditGrantRules';

interface Props {
  account: CreditAccountDTO;
  /** Credit reach over the account: offers Refund on its usage charges. */
  canRefund?: boolean;
  onClose: () => void;
}

const filterInput =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';

/**
 * One account's ledger, newest first: every charge with its billed minutes, carrier
 * and — for a provider — the business it paid for. This is what a usage invoice is
 * built from, so it is the place to check one.
 */
const LedgerModal: React.FC<Props> = ({ account, canRefund = false, onClose }) => {
  const [rows, setRows] = useState<CreditTransactionDTO[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [type, setType] = useState<TransactionType | ''>('');
  /** IST days; ledger times are UTC, so the range is converted to UTC for the server. */
  const [dates, setDates] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** The charge whose refund is being confirmed. */
  const [refunding, setRefunding] = useState<CreditTransactionDTO | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const confirmRefund = async () => {
    if (!refunding) return;
    setRefundBusy(true);
    setRefundError(null);
    const r = await billingService.refundTransaction(refunding.id, refundReason.trim() || undefined);
    setRefundBusy(false);
    if (r.success) {
      setNotice(`Refunded ${money(r.data?.amount ?? Math.abs(refunding.amount))} for charge #${refunding.id}.`);
      setRefunding(null);
      setRefundReason('');
      setVersion((v) => v + 1);
    } else {
      setRefundError(r.error || 'Failed to refund');
    }
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    billingService
      .getTransactions({
        organizationId: account.organizationId,
        type: type || undefined,
        ...istDaysToServerUtc(dates.from, dates.to),
        page,
        size: 50,
      })
      .then((r) => {
        if (!live) return;
        if (r.success && r.data) {
          setRows(r.data.content);
          setTotalPages(r.data.totalPages);
          setTotalElements(r.data.totalElements);
          setError(null);
        } else {
          setError(r.error || 'Failed to load the ledger');
        }
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [account.organizationId, type, dates, page, version]);

  const money = (v?: number) => formatMoney(v ?? 0, account.currency || 'INR');
  const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
  const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] flex flex-col mx-4">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">
              Ledger · {account.organizationName || account.organizationId}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Balance {money(account.creditBalance)} · {totalElements} transaction(s) · times in IST
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <IstDateRange
              label="Date"
              from={dates.from}
              to={dates.to}
              inputClassName={filterInput}
              onChange={(r) => {
                setDates(r);
                setPage(1);
              }}
            />
            <select
              aria-label="Transaction type"
              value={type}
              onChange={(e) => {
                setType(e.target.value as TransactionType | '');
                setPage(1);
              }}
              className={filterInput}
            >
              <option value="">All types</option>
              {Object.entries(transactionTypeLabels).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            <button onClick={onClose} className="self-start text-gray-400 hover:text-gray-600 text-2xl leading-none" aria-label="Close">
              &times;
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        {notice && <p className="text-sm text-green-700 dark:text-green-400 mb-2">{notice}</p>}
        {refunding && (
          <div className="rounded-lg border p-3 mb-3 space-y-2 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              Refund charge #{refunding.id} ({money(Math.abs(refunding.amount))}, {formatServerUtcAsIst(refunding.createdAt)} IST)?
              The amount is credited back to the account.
            </p>
            <input
              aria-label="Refund reason"
              placeholder="Reason (optional)"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              className={`${filterInput} w-full`}
            />
            {refundError && <p className="text-sm text-red-600">{refundError}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRefunding(null);
                  setRefundError(null);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={refundBusy}
                onClick={confirmRefund}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {refundBusy ? 'Refunding…' : 'Confirm refund'}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-auto flex-1 border border-gray-200 dark:border-gray-700 rounded">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th className={`${th} text-left`}>When</th>
                <th className={`${th} text-left`}>Type</th>
                <th className={`${th} text-left`}>Service</th>
                <th className={`${th} text-left`}>For</th>
                <th className={`${th} text-left`}>Carrier</th>
                <th className={`${th} text-right`}>Minutes</th>
                <th className={`${th} text-right`}>Amount</th>
                <th className={`${th} text-right`}>Balance</th>
                <th className={`${th} text-left`}>Reference</th>
                {canRefund && <th className={`${th} text-right`}>Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={canRefund ? 10 : 9} className="px-3 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={canRefund ? 10 : 9} className="px-3 py-6 text-center text-sm text-gray-500">No transactions</td></tr>
              ) : (
                rows.map((t) => (
                  <tr key={t.id} title={t.description || ''}>
                    <td className={td}>{formatServerUtcAsIst(t.createdAt)}</td>
                    <td className={td}>{transactionTypeLabels[t.transactionType] || t.transactionType}</td>
                    <td className={td}>{t.serviceType ? serviceTypeLabels[t.serviceType] || t.serviceType : '-'}</td>
                    <td className={td}>{t.onBehalfOfOrganizationName || t.onBehalfOfOrganizationId || '-'}</td>
                    <td className={td}>{t.carrier || '-'}</td>
                    <td className={`${td} text-right`}>{t.billedMinutes ?? '-'}</td>
                    <td className={`${td} text-right ${String(t.transactionType).startsWith('DEBIT') ? 'text-red-600' : 'text-green-700'}`}>{money(t.amount)}</td>
                    <td className={`${td} text-right`}>{money(t.balanceAfter)}</td>
                    <td className={`${td} font-mono text-xs text-gray-500`}>{t.referenceId || '-'}</td>
                    {canRefund && (
                      <td className={`${td} text-right`}>
                        {isRefundable(t, account.billingType) && (
                          <button
                            type="button"
                            onClick={() => {
                              setRefunding(t);
                              setRefundReason('');
                              setRefundError(null);
                              setNotice(null);
                            }}
                            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                          >
                            Refund
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between pt-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Page {totalPages === 0 ? 0 : page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LedgerModal;
